// Enquiries ↔ Site Plan: a reserved enquiry with an earmarked unit holds that unit (status "reserved",
// under the person's name), but only if the unit is empty. When the enquiry stops being reserved, moves
// to another unit, or is converted, archived or deleted, the old unit is freed — but only if it is still
// reserved under this person's name, so nobody else's unit is ever changed.

/** Which units an enquiry change touches: the unit it held before, and the unit it should hold now. */
export function reservationUnits(before, after) {
  const held = e => (e?.status === "reserved" && e.earmarked_unit) ? String(e.earmarked_unit) : "";
  return { oldId: held(before), newId: held(after) };
}

const sameName = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase() && String(a || "").trim() !== "";
// Empty = marked Available/vacant (or no status) AND no customer details on it at all, so a reservation can never
// overwrite anyone's name, email or phone.
const blank = v => !String(v ?? "").trim();
export const isEmptyUnit = u => !!u && (u.status === "available" || u.status === "vacant" || !u.status) && blank(u.tenant) && blank(u.email) && blank(u.phone);

/**
 * What to do, given the enquiry before/after and the current state of the old and new units (fresh from the database):
 *   free    — set the old unit back to Available
 *   reserve — set the new unit to Reserved under the person's name
 *   blocked — the new unit isn't empty, so it was left alone (tell the user)
 */
export function planReservation({ before, after, oldUnit, newUnit }) {
  const { oldId, newId } = reservationUnits(before, after);
  const free = !!oldId && oldId !== newId && oldUnit?.status === "reserved" && sameName(oldUnit.tenant, before?.name);
  const ours = newUnit?.status === "reserved" && (sameName(newUnit.tenant, after?.name) || sameName(newUnit.tenant, before?.name));
  const reserve = !!newId && !!newUnit && (isEmptyUnit(newUnit) || ours);
  const blocked = !!newId && !!newUnit && !reserve;
  return { oldId, newId, free, reserve, blocked };
}
